import { isTruthy } from '../shared/is-truthy';
import { isStatementEmailPayment } from './pledge-pay-surface';
import { getPledgePaySurchargeAmt } from './pledge-pay-config';
import { cleanupBootstrapModalBody } from '../shared/bootstrap-modal-cleanup.js';

function getCsrfToken() {
    const metaToken = document.querySelector('meta[name="csrf-token"]');
    return metaToken ? metaToken.getAttribute('content') : '';
}

function getPaymentMode($container) {
    return $container.find('input[name="payment_mode"]:checked').val()
        || $container.find('#payment_mode').val();
}

function isPledgePayRecurringDurationReady($container) {
    if (getPaymentMode($container) !== 'recurring') {
        return true;
    }
    const raw = String($container.find('#pledge_payment_duration').val() ?? '');
    if (raw === '') {
        return false;
    }
    const duration = parseInt(raw, 10);
    return duration === 0 || duration >= 2;
}

function pledgePayUiRoot($anyDescendant) {
    const $modal = $anyDescendant.closest('#setPledgePaymentModal');
    if ($modal.length) {
        return $modal;
    }
    const $body = $anyDescendant.closest('#pledge_pay_body');
    return $body.length ? $body : $anyDescendant;
}

function pledgePayBodyFromShell($shell) {
    if ($shell.is('#pledge_pay_body')) {
        return $shell;
    }
    const $body = $shell.find('#pledge_pay_body').first();
    return $body.length ? $body : $shell;
}

const CREDIT_COVERED_CLASS = 'is-credit-covered';

function isPledgePayCreditCovered($shell) {
    return pledgePayBodyFromShell($shell).hasClass(CREDIT_COVERED_CLASS);
}

function setPledgePayCreditCovered($shell, covered) {
    const $body = pledgePayBodyFromShell($shell);
    $body.toggleClass(CREDIT_COVERED_CLASS, covered);
    $body.find('[data-pledge-express-pay], [data-card-swipe]').prop('inert', covered);
}

function isMgrPledgePayShell($shell) {
    return $shell.is('#setPledgePaymentModal')
        || $shell.is('#pledge_pay_body[data-pledge-pay-surface="manager_page"]');
}

function isManagerPledgePaymentModal($el) {
    return $el.closest('#setPledgePaymentModal').length > 0;
}

function shouldFilterStatementPledges($container, paymentMode) {
    if (!isStatementEmailPayment()) {
        return false;
    }
    if (paymentMode === 'recurring') {
        return false;
    }
    const payType = $container.find('#payment_type').val();
    return payType !== 'mark';
}

function hasPendingMatanah($container) {
    return $container.find('.matanah-save-btn').length > 0;
}

function formatCurrency(amount) {
    const parsed = parsePledgeMoneyAmount(amount);
    if (Number.isNaN(parsed)) {
        return '0.00';
    }
    return parsed.toFixed(2);
}

function parsePledgeMoneyAmount(amount) {
    if (amount === null || amount === undefined) {
        return NaN;
    }
    if (typeof amount === 'number') {
        return Number.isFinite(amount) ? amount : NaN;
    }
    const normalized = String(amount).replace(/[$,\s]/g, '');
    if (normalized === '') {
        return NaN;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : NaN;
}

function formatCampaignListOxford(names) {
    const list = (names || []).map((n) => String(n).trim()).filter(Boolean);
    if (!list.length) {
        return '';
    }
    if (list.length === 1) {
        return list[0];
    }
    if (list.length === 2) {
        return `${list[0]} and ${list[1]}`;
    }
    return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`;
}

function formatCampaignNounPhrase(names) {
    const list = [...new Set((names || []).map((n) => String(n).trim()).filter(Boolean))];
    if (!list.length) {
        return '';
    }
    if (list.length === 1) {
        return `${list[0]} campaign`;
    }
    return `${formatCampaignListOxford(list)} campaigns`;
}

function formatSurchargePercent() {
    const percent = (getPledgePaySurchargeAmt() * 100);
    if (!percent) return '0';
    const rounded = Math.round(percent * 100) / 100;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

function mgrPledgeBillMemberAjaxUrl(baseUrl, segment) {
    const base = String(baseUrl || '').trim().replace(/\/+$/, '');
    if (!base) {
        return '';
    }
    const joined =
        segment !== undefined && segment !== null && String(segment).trim() !== ''
            ? `${base}/${encodeURIComponent(String(segment).trim())}`
            : base;
    if (/^https?:\/\//i.test(joined)) {
        return joined;
    }
    const path = joined.startsWith('/') ? joined : `/${joined}`;
    return `${window.location.origin}${path}`;
}

function pledgePayAmountDue(pledge) {
    const balanceDue = parsePledgeMoneyAmount(pledge.balance_due);
    return Number.isNaN(balanceDue) ? 0 : balanceDue;
}

function isMatanahAmountPending(pledge, useAmount = null) {
    const due = useAmount == null ? pledgePayAmountDue(pledge) : useAmount;
    return isTruthy(pledge.matanah) && !(due > 0);
}

function mgrPledgeBillShellDismissIfModal($shell) {
    if ($shell && $shell.length && $shell.is('#setPledgePaymentModal')) {
        $shell.modal('hide');
        cleanupBootstrapModalBody();
    }
}

function runAfterPledgePaymentModalHidden($shell, callback) {
    const $modal = $shell && $shell.length
        ? ($shell.is('#setPledgePaymentModal') ? $shell : $shell.closest('#setPledgePaymentModal'))
        : window.jQuery();
    if (!$modal.length || !$modal.hasClass('show')) {
        callback();
        return;
    }
    let ran = false;
    const run = () => {
        if (ran) {
            return;
        }
        ran = true;
        callback();
    };
    $modal.one('hidden.bs.modal', run);
    window.setTimeout(run, 1000);
}

const DEFAULT_FULLY_COVERED_SUCCESS_DELAY_MS = 2000;

function mgrPledgeBillRunAfterFullyCoveredDelay(callback) {
    setTimeout(callback, DEFAULT_FULLY_COVERED_SUCCESS_DELAY_MS);
}

export {
    formatCampaignListOxford,
    formatCampaignNounPhrase,
    formatCurrency,
    formatSurchargePercent,
    getCsrfToken,
    getPaymentMode,
    hasPendingMatanah,
    isPledgePayRecurringDurationReady,
    isManagerPledgePaymentModal,
    isMatanahAmountPending,
    isMgrPledgePayShell,
    isPledgePayCreditCovered,
    mgrPledgeBillMemberAjaxUrl,
    mgrPledgeBillRunAfterFullyCoveredDelay,
    mgrPledgeBillShellDismissIfModal,
    runAfterPledgePaymentModalHidden,
    setPledgePayCreditCovered,
    parsePledgeMoneyAmount,
    pledgePayAmountDue,
    pledgePayBodyFromShell,
    pledgePayUiRoot,
    shouldFilterStatementPledges,
};
